import {
  Badge,
  Button,
  Empty,
  Modal,
  Space,
  Table,
  Tooltip,
} from "antd";
import Column from "antd/es/table/Column";
import Link from "antd/es/typography/Link";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { api } from "../../../config";
import { AuthenticationState } from "../../../interfaces/users";
import {
  formatSeniority,
  getProductsAssembleTime,
  getProductsSeniority,
} from "../../../utils/helpers/ticketHelpers";
import ScheduleAssembleEvent from "./ScheduleAssembleEvent";

interface RelatedPendingScheduleTicketsModalProps {
  open: boolean;
  taxId?: string;
  excludeId?: number;
  onClose: () => void;
  onScheduled?: () => void;
}

const getTicketErrors = (ticket: any) => {
  const errors = [];

  if (!ticket?.products?.length) {
    errors.push("Produtos não cadastrados");
    return errors;
  }

  if (
    !getProductsAssembleTime(ticket.products) ||
    !getProductsSeniority(ticket.products)
  ) {
    errors.push("Produto não cadastrado");
    return errors;
  }

  for (const product of ticket.products) {
    if (!product.product?.seniorityId || !product.product?.assemblyTime) {
      errors.push("Produtos não cadastrados");
      break;
    }
  }

  return errors;
};

const RelatedPendingScheduleTicketsModal = ({
  open,
  taxId,
  excludeId,
  onClose,
  onScheduled,
}: RelatedPendingScheduleTicketsModalProps) => {
  const { token } = useSelector((state: AuthenticationState) => state);
  const [tickets, setTickets] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [scheduleTicket, setScheduleTicket] = useState<any>();
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);

  const fetchTickets = useCallback(async () => {
    if (!taxId) {
      setTickets([]);
      return;
    }

    try {
      setLoading(true);
      const response = await api(token).get(
        `tickets/assemble/pending-schedule-by-tax-id?taxId=${encodeURIComponent(
          taxId
        )}${excludeId ? `&excludeId=${excludeId}` : ""}`
      );
      const relatedTickets = response ?? [];
      setTickets(relatedTickets);

      if (!relatedTickets.length) {
        onClose();
      }
    } catch (error) {
      console.log(error);
      toast.error("Ocorreu um erro ao buscar tickets do mesmo cliente");
    } finally {
      setLoading(false);
    }
  }, [excludeId, onClose, taxId, token]);

  useEffect(() => {
    if (open) {
      fetchTickets();
    }
  }, [fetchTickets, open]);

  const dataSource = useMemo(
    () =>
      tickets.map((ticket) => ({
        ...ticket,
        key: ticket.id,
        errors: getTicketErrors(ticket),
        period: getProductsAssembleTime(ticket.products),
        seniority: getProductsSeniority(ticket.products),
      })),
    [tickets]
  );

  const handleSchedule = (ticket: any) => {
    setScheduleTicket(ticket);
    setScheduleModalOpen(true);
  };

  const handleScheduled = async () => {
    setScheduleModalOpen(false);
    setScheduleTicket(undefined);
    onScheduled?.();
    await fetchTickets();
  };

  return (
    <>
      <Modal
        title="Outros tickets pendentes para o mesmo cliente"
        open={open}
        onCancel={onClose}
        footer={null}
        width={1000}
      >
        <Table
          loading={loading}
          dataSource={dataSource}
          pagination={false}
          locale={{
            emptyText: (
              <Empty description="Nenhum outro ticket pendente de agendamento para este cliente" />
            ),
          }}
        >
          <Column
            title="O.S."
            dataIndex="title"
            key="title"
            render={(title: string, record: any) => (
              <Link href={`/montagem?id=${record.id}`} target="_blank">
                <b>{title}</b>
              </Link>
            )}
          />
          <Column title="Venda" dataIndex="saleId" key="saleId" />
          <Column
            title="Endereço"
            key="address"
            render={(_: any, record: any) => {
              const address = record.client?.deliveryAddress;
              return [
                address?.street,
                address?.neighbor,
                address?.city,
                address?.state,
              ]
                .filter(Boolean)
                .join(" - ");
            }}
          />
          <Column
            title="Tempo"
            dataIndex="period"
            key="period"
            render={(period: number) => (period ? `${period} horas` : "-")}
          />
          <Column
            title="Senioridade"
            dataIndex="seniority"
            key="seniority"
            render={(seniority: number) =>
              seniority ? formatSeniority(seniority) : "-"
            }
          />
          <Column
            title="Ações"
            key="actions"
            render={(_: any, record: any) => (
              <Space>
                {record.errors.length ? (
                  <Tooltip
                    title={record.errors.map((error: string) => (
                      <div key={error}>{error}</div>
                    ))}
                    color="red"
                    arrow={false}
                  >
                    <Badge
                      count={
                        <ExclamationCircleOutlined
                          style={{ color: "#f5222d", fontSize: 16 }}
                        />
                      }
                    >
                      <Button type="primary" disabled>
                        Agendar
                      </Button>
                    </Badge>
                  </Tooltip>
                ) : (
                  <Button type="primary" onClick={() => handleSchedule(record)}>
                    Agendar
                  </Button>
                )}
              </Space>
            )}
          />
        </Table>
      </Modal>

      {scheduleModalOpen && (
        <Modal
          title="Agendar Ticket"
          open={scheduleModalOpen}
          width={800}
          footer={null}
          onCancel={() => setScheduleModalOpen(false)}
        >
          <ScheduleAssembleEvent
            initialTicket={scheduleTicket}
            onSchedule={handleScheduled}
            onCancel={() => setScheduleModalOpen(false)}
          />
        </Modal>
      )}
    </>
  );
};

export default RelatedPendingScheduleTicketsModal;
